import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Check, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { Project, ProjectMilestone, ProjectSubtask } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import {
  LogCalendarTimeModal,
  type CalendarTimeTarget,
} from '../components/LogCalendarTimeModal.tsx';
import { useToast } from '../components/Toaster.tsx';
import { bucketLabel } from '../timer/buckets.ts';
import type { BillingBucket } from '../timer/buckets.ts';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

type DotKind = 'due' | 'cutover' | 'subtask' | 'checkin' | 'milestone' | 'event' | 'external';
type View = 'mine' | 'team';
type Scope = 'day' | 'week' | 'month';

// Calendar display timezone. Defaults to America/New_York per T3 standard;
// persisted in localStorage so the user's choice survives reloads.
const TZ_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST, no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'UTC', label: 'UTC' },
];
const TZ_STORAGE_KEY = 't3.calendar.timezone';
const DEFAULT_TZ = 'America/New_York';

function formatYmdInTz(date: Date, timeZone: string): string {
  // en-CA gives YYYY-MM-DD ordering; formatToParts lets us assemble safely.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${y}-${m}-${d}`;
}

function formatTimeInTz(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat([], {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

// Format an absolute Date as the wall-clock string a `<input type="datetime-local">`
// expects (`YYYY-MM-DDTHH:mm`), expressed in the given timezone.
function formatLocalInputInTz(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

// Interpret a `YYYY-MM-DDTHH:mm` wall-clock string as a moment in the given
// timezone and return the corresponding UTC Date. Two-pass: figure out the
// timezone offset for that instant, then apply it.
function zonedInputToUtc(wallClock: string, timeZone: string): Date {
  const [datePart, timePart] = wallClock.split('T');
  if (!datePart || !timePart) return new Date(NaN);
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (y === undefined || m === undefined || d === undefined || hh === undefined || mm === undefined) {
    return new Date(NaN);
  }
  const asUtcMs = Date.UTC(y, m - 1, d, hh, mm);
  const probe = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(asUtcMs));
  const get = (t: string) => Number(probe.find((p) => p.type === t)?.value ?? 0);
  let probeHour = get('hour');
  if (probeHour === 24) probeHour = 0;
  const probeAsUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    probeHour,
    get('minute'),
    get('second'),
  );
  const offsetMs = probeAsUtcMs - asUtcMs;
  return new Date(asUtcMs - offsetMs);
}

// Short label for the active timezone (e.g. "ET", "PT") — used inline next to
// time inputs/labels so the user sees which zone the value is in.
function tzShortLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

interface CalDot {
  kind: DotKind;
  label: string;
  href: string;
  org?: string;
  done?: boolean;
  assignedAgentId: string | null; // null => not personally assigned (project-wide)
  eventId?: string;     // set for kind === 'event' so the dot can open detail
  checkInId?: string;   // set for kind === 'checkin'
  meetingUrl?: string;  // surfaced on check-in + event dots when available
  // Present on dots that represent a real meeting (Google event, team event,
  // check-in). Drives the "+" button that logs the meeting as time.
  logTarget?: CalendarTimeTarget;
}

// A time entry already logged from a calendar event, keyed by that event.
interface LoggedEntry {
  timeEntryId: string;
  externalCalendarEventId: string;
  durationMinutes: number;
  billingBucket: BillingBucket;
  organizationName: string;
}

// Minutes between two ISO instants, clamped to what a time entry accepts.
// All-day events carry no real length, so they fall back to an hour the agent
// can adjust in the modal.
function meetingMinutes(startUtc: string, endUtc: string, allDay: boolean): number {
  if (allDay) return 60;
  const ms = new Date(endUtc).getTime() - new Date(startUtc).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 60;
  return Math.min(24 * 60, Math.max(1, Math.round(ms / 60000)));
}

interface CheckInLite {
  id: string;
  organizationId: string;
  organizationName: string;
  scheduledAt: string;
  title: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  assignedAgentId: string | null;
  meetingUrl: string | null;
  attendeeEmails: string[];
}

interface ExternalEvent {
  id: string;
  calendarId: string;
  calendarSummary: string | null;
  backgroundColor: string | null;
  summary: string;
  description: string | null;
  location: string | null;
  htmlLink: string | null;
  startUtc: string;
  endUtc: string;
  allDay: boolean;
  hangoutLink: string | null;
  // Which client the attendees point at, resolved server-side. Pre-picks the
  // customer when logging the meeting as time.
  suggestedOrganizationId: string | null;
  suggestedOrganizationName: string | null;
  suggestedOrganizationMatchedOn: 'contact' | 'domain' | null;
}

interface CalendarEventLite {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  organizerAgentId: string;
  organizerAgentName: string | null;
  scheduledAt: string;
  durationMinutes: number;
  title: string;
  description: string | null;
  location: string | null;
  attendeeEmails: string[];
  teamsJoinUrl: string | null;
  googleHtmlLink: string | null;
  status: 'scheduled' | 'cancelled';
}

export function CalendarPage(): React.ReactElement {
  useDocumentTitle('Calendar');
  const { agent } = useAuth();
  const myAgentId = agent?.id ?? null;

  const [view, setView] = useState<View>('mine');
  const [scope, setScope] = useState<Scope>('month');
  const [timeZone, setTimeZone] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(TZ_STORAGE_KEY);
      if (saved && TZ_OPTIONS.some((t) => t.value === saved)) return saved;
    } catch {
      /* ignore */
    }
    return DEFAULT_TZ;
  });
  useEffect(() => {
    try {
      localStorage.setItem(TZ_STORAGE_KEY, timeZone);
    } catch {
      /* ignore */
    }
  }, [timeZone]);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [subtasksByProject, setSubtasksByProject] = useState<Map<string, ProjectSubtask[]>>(new Map());
  const [milestonesByProject, setMilestonesByProject] = useState<Map<string, ProjectMilestone[]>>(new Map());
  const [checkIns, setCheckIns] = useState<CheckInLite[]>([]);
  const [events, setEvents] = useState<CalendarEventLite[]>([]);
  const [externalEvents, setExternalEvents] = useState<ExternalEvent[]>([]);
  const [externalLoaded, setExternalLoaded] = useState(false);
  const [externalError, setExternalError] = useState<string | null>(null);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [logTarget, setLogTarget] = useState<CalendarTimeTarget | null>(null);
  const [logged, setLogged] = useState<Map<string, LoggedEntry>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [cursor, setCursor] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ projects: Project[] }>('/api/projects?status=active')
      .then(async (r) => {
        if (cancelled) return;
        setProjects(r.projects);
        const subtaskMap = new Map<string, ProjectSubtask[]>();
        const milestoneMap = new Map<string, ProjectMilestone[]>();
        await Promise.all(
          r.projects.flatMap((p) => [
            (async () => {
              try {
                const sr = await apiFetch<{ subtasks: ProjectSubtask[] }>(
                  `/api/projects/${p.id}/subtasks`,
                );
                subtaskMap.set(p.id, sr.subtasks);
              } catch {
                /* skip */
              }
            })(),
            (async () => {
              try {
                const mr = await apiFetch<{ milestones: ProjectMilestone[] }>(
                  `/api/projects/${p.id}/milestones`,
                );
                milestoneMap.set(p.id, mr.milestones);
              } catch {
                /* skip */
              }
            })(),
          ]),
        );
        if (!cancelled) {
          setSubtasksByProject(subtaskMap);
          setMilestonesByProject(milestoneMap);
        }
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load projects.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const from = new Date(cursor.getFullYear(), cursor.getMonth() - 2, 1).toISOString();
    const to = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0).toISOString();
    apiFetch<{ checkIns: CheckInLite[] }>(
      `/api/check-ins?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => {
        if (!cancelled) setCheckIns(r.checkIns);
      })
      .catch(() => {
        if (!cancelled) setCheckIns([]);
      });
    apiFetch<{ events: CalendarEventLite[] }>(
      `/api/calendar-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => {
        if (!cancelled) setEvents(r.events);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [cursor]);

  useEffect(() => {
    let cancelled = false;
    if (view !== 'mine') {
      setExternalEvents([]);
      setExternalError(null);
      setExternalLoaded(false);
      return;
    }
    const from = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString();
    const to = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0).toISOString();
    apiFetch<{ events: ExternalEvent[] }>(
      `/api/profile/google/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => {
        if (cancelled) return;
        setExternalEvents(r.events);
        setExternalError(null);
        setExternalLoaded(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Don't blank previously-loaded events on a transient failure — surface
        // the error so we can see what's wrong instead of silently emptying the
        // calendar.
        const msg =
          err instanceof ApiError
            ? `${err.message} (status ${err.status}, code ${err.code})`
            : err instanceof Error
              ? `${err.name}: ${err.message}`
              : String(err);
        setExternalError(msg);
        setExternalLoaded(true);
        console.error('[calendar] external events fetch failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [cursor, view]);

  function reloadEvents(): void {
    const from = new Date(cursor.getFullYear(), cursor.getMonth() - 2, 1).toISOString();
    const to = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0).toISOString();
    apiFetch<{ events: CalendarEventLite[] }>(
      `/api/calendar-events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => setEvents(r.events))
      .catch(() => undefined);
  }

  // Which meetings in view already became time entries. Window is deliberately
  // wider than the visible month so navigating a month doesn't briefly show a
  // logged meeting as unlogged.
  const reloadLogged = useCallback((): void => {
    const from = new Date(cursor.getFullYear(), cursor.getMonth() - 2, 1).toISOString();
    const to = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 1).toISOString();
    apiFetch<{ entries: LoggedEntry[] }>(
      `/api/timesheets/calendar-linked?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((r) => setLogged(new Map(r.entries.map((e) => [e.externalCalendarEventId, e]))))
      .catch(() => undefined);
  }, [cursor]);

  useEffect(() => {
    reloadLogged();
  }, [reloadLogged]);

  const dotsByDate = useMemo(() => {
    const map = new Map<string, CalDot[]>();
    function add(date: string, dot: CalDot) {
      // Strict separation: My calendar = external personal calendars only;
      // Team calendar = T3-internal items only. Externals carry kind ===
      // 'external'; everything else is internal.
      if (view === 'mine' && dot.kind !== 'external') return;
      if (view === 'team' && dot.kind === 'external') return;
      const arr = map.get(date) ?? [];
      arr.push(dot);
      map.set(date, arr);
    }
    for (const p of projects ?? []) {
      if (p.dueDate) {
        add(p.dueDate, {
          kind: 'due',
          label: `${p.name} (due)`,
          href: `/projects/${p.id}`,
          org: p.organizationName,
          assignedAgentId: null,
        });
      }
      if (p.cutoverDate) {
        add(p.cutoverDate, {
          kind: 'cutover',
          label: `${p.name} (cutover)`,
          href: `/projects/${p.id}`,
          org: p.organizationName,
          assignedAgentId: null,
        });
      }
      const subs = subtasksByProject.get(p.id) ?? [];
      for (const s of subs) {
        if (s.dueDate) {
          add(s.dueDate, {
            kind: 'subtask',
            label: `${p.name}: ${s.title}`,
            href: `/projects/${p.id}`,
            org: p.organizationName,
            done: s.completedAt !== null,
            assignedAgentId: s.assignedAgentId,
          });
        }
      }
      const milestones = milestonesByProject.get(p.id) ?? [];
      for (const m of milestones) {
        add(m.milestoneDate, {
          kind: 'milestone',
          label: `★ ${p.name}: ${m.title}`,
          href: `/projects/${p.id}`,
          org: p.organizationName,
          done: m.completedAt !== null,
          assignedAgentId: null, // milestones are project-wide; show in Team only
        });
      }
    }
    for (const c of checkIns) {
      const d = new Date(c.scheduledAt);
      const ymd = formatYmdInTz(d, timeZone);
      const time = formatTimeInTz(d, timeZone);
      add(ymd, {
        kind: 'checkin',
        label: `${time} · ${c.title}`,
        href: `/organizations/${c.organizationId}`,
        org: c.organizationName,
        done: c.status === 'completed',
        assignedAgentId: c.assignedAgentId,
        checkInId: c.id,
        meetingUrl: c.meetingUrl ?? undefined,
        logTarget: {
          // Check-ins carry no scheduled length, so the modal opens at an hour.
          externalCalendarEventId: `t3-checkin::${c.id}`,
          title: c.title,
          startUtc: c.scheduledAt,
          durationMinutes: 60,
          organizationId: c.organizationId,
          organizationName: c.organizationName,
        },
      });
    }
    for (const e of events) {
      if (e.status === 'cancelled') continue;
      const d = new Date(e.scheduledAt);
      const ymd = formatYmdInTz(d, timeZone);
      const time = formatTimeInTz(d, timeZone);
      add(ymd, {
        kind: 'event',
        label: `${time} · ${e.title}`,
        href: '#',
        org: e.organizationName ?? undefined,
        assignedAgentId: e.organizerAgentId,
        eventId: e.id,
        meetingUrl: e.teamsJoinUrl ?? undefined,
        logTarget: {
          externalCalendarEventId: `t3-event::${e.id}`,
          title: e.title,
          startUtc: e.scheduledAt,
          durationMinutes: e.durationMinutes,
          organizationId: e.organizationId,
          organizationName: e.organizationName,
        },
      });
    }
    for (const ev of externalEvents) {
      const d = new Date(ev.startUtc);
      const ymd = formatYmdInTz(d, timeZone);
      const time = ev.allDay ? 'All day' : formatTimeInTz(d, timeZone);
      add(ymd, {
        kind: 'external',
        label: `${time} · ${ev.summary}`,
        href: ev.htmlLink ?? '#',
        org: ev.calendarSummary ?? undefined,
        assignedAgentId: myAgentId,
        logTarget: {
          externalCalendarEventId: ev.id,
          title: ev.summary,
          startUtc: ev.startUtc,
          durationMinutes: meetingMinutes(ev.startUtc, ev.endUtc, ev.allDay),
          suggestedOrganizationId: ev.suggestedOrganizationId,
          suggestedOrganizationName: ev.suggestedOrganizationName,
          suggestedMatchedOn: ev.suggestedOrganizationMatchedOn,
        },
      });
    }
    return map;
  }, [projects, subtasksByProject, milestonesByProject, checkIns, events, externalEvents, view, myAgentId, timeZone]);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const weekStart = new Date(cursor);
  weekStart.setDate(cursor.getDate() - cursor.getDay());

  const cells: Array<Date | null> = [];
  if (scope === 'month') {
    const firstWeekday = monthStart.getDay();
    const daysInMonth = monthEnd.getDate();
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
  } else if (scope === 'week') {
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      cells.push(d);
    }
  } else {
    cells.push(new Date(cursor));
  }

  // "Today" is computed in the displayed timezone so the highlight matches what
  // the user is seeing — e.g. 11pm PST shows tomorrow's cell as "today" when
  // viewing in ET.
  const todayYmd = formatYmdInTz(new Date(), timeZone);

  let scopeLabel: string;
  if (scope === 'month') {
    scopeLabel = monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } else if (scope === 'week') {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
    scopeLabel = sameMonth
      ? `${weekStart.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} – ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
      : `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${weekEnd.getFullYear()}`;
  } else {
    scopeLabel = cursor.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function navigate(delta: 1 | -1): void {
    const d = new Date(cursor);
    if (scope === 'day') d.setDate(d.getDate() + delta);
    else if (scope === 'week') d.setDate(d.getDate() + 7 * delta);
    else d.setMonth(d.getMonth() + delta);
    setCursor(d);
  }

  function goToday(): void {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), t.getDate()));
  }

  const dotLimit = scope === 'month' ? 4 : scope === 'week' ? 12 : Number.POSITIVE_INFINITY;
  const cellMinHeight =
    scope === 'month' ? 'min-h-[88px]' : scope === 'week' ? 'min-h-[360px]' : 'min-h-[480px]';
  const gridCols = scope === 'day' ? 'grid-cols-1' : 'grid-cols-7';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Calendar</h1>
          <p className="mt-1 text-[var(--color-muted)]">
            {view === 'mine'
              ? 'Your personal calendars (configure under My profile → My Google calendar). Strictly your own events — no T3-internal items.'
              : 'T3-internal: scheduled check-ins, project due/cutover dates, subtask deadlines, milestones, and team events. No personal calendars.'}
          </p>
          <p className="mt-1 flex items-center gap-1 text-sm text-[var(--color-muted)]">
            Hit
            <span className="inline-flex items-center rounded border border-[var(--color-border)] bg-white p-0.5 text-[var(--color-t3-blue)]">
              <Plus className="h-3 w-3" />
            </span>
            on any meeting to log it as time against a client — no ticket needed.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-full border border-[var(--color-border)] bg-white p-1">
            <TabBtn active={view === 'mine'} onClick={() => setView('mine')}>
              My calendar
            </TabBtn>
            <TabBtn active={view === 'team'} onClick={() => setView('team')}>
              Team calendar
            </TabBtn>
          </div>
          <div className="flex gap-1 rounded-full border border-[var(--color-border)] bg-white p-1">
            <TabBtn active={scope === 'day'} onClick={() => setScope('day')}>
              Day
            </TabBtn>
            <TabBtn active={scope === 'week'} onClick={() => setScope('week')}>
              Week
            </TabBtn>
            <TabBtn active={scope === 'month'} onClick={() => setScope('month')}>
              Month
            </TabBtn>
          </div>
          <label className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
            <span className="sr-only">Timezone</span>
            <select
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              title="Display all calendar times in this timezone"
              className="rounded-full border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--color-header)]"
            >
              {TZ_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <Button onClick={() => setShowNewEvent(true)}>+ New event</Button>
        </div>
      </header>

      {showNewEvent && (
        <NewEventModal
          timeZone={timeZone}
          onClose={() => setShowNewEvent(false)}
          onCreated={() => {
            setShowNewEvent(false);
            reloadEvents();
          }}
        />
      )}

      {logTarget && (
        <LogCalendarTimeModal
          target={logTarget}
          timeZone={timeZone}
          onClose={() => setLogTarget(null)}
          onSaved={({ minutes, bucket, orgName }) => {
            setLogTarget(null);
            reloadLogged();
            toast.success(
              `Logged ${minutes} min — ${bucketLabel(bucket)}${orgName ? ` · ${orgName}` : ''}`,
            );
          }}
        />
      )}

      {selectedEventId && (
        <EventDetailModal
          event={events.find((e) => e.id === selectedEventId) ?? null}
          timeZone={timeZone}
          logged={logged.get(`t3-event::${selectedEventId}`) ?? null}
          onLogTime={(t) => setLogTarget(t)}
          onClose={() => setSelectedEventId(null)}
          onCancelled={() => {
            setSelectedEventId(null);
            reloadEvents();
          }}
          onUpdated={() => {
            // Keep the modal open so the new Join button appears immediately.
            reloadEvents();
          }}
        />
      )}

      {view === 'mine' && !externalLoaded && !externalError && (
        <div className="flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-muted)]">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-t3-blue)] border-t-transparent" />
          Loading your Google calendar events…
        </div>
      )}
      {view === 'mine' && externalError && (
        <div className="rounded-[var(--radius-card)] border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 px-4 py-3 text-sm text-[var(--color-danger)]">
          <div className="font-semibold">Couldn't load your Google calendar events</div>
          <div className="mt-1 text-[var(--color-body)]">{externalError}</div>
          <div className="mt-1 text-xs text-[var(--color-muted)]">
            Check Profile → My Google calendar — make sure at least one calendar checkbox is ticked.
            If the connection looks healthy, disconnect and reconnect to refresh the token.
          </div>
        </div>
      )}
      {view === 'mine' && externalLoaded && !externalError && externalEvents.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-muted)]">
          No events from your Google calendars in this window. Either no events are scheduled, or no
          calendars are enabled under Profile → My Google calendar.
        </div>
      )}

      <Card>
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-[var(--color-header)]">{scopeLabel}</h2>
            <button
              type="button"
              onClick={goToday}
              className="rounded-full border border-[var(--color-border)] bg-white px-3 py-1 text-xs font-semibold text-[var(--color-muted)] hover:text-[var(--color-header)]"
            >
              Today
            </button>
          </div>
          <Button variant="outline" onClick={() => navigate(1)}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

        {scope !== 'day' && (
          <div className="mt-4 grid grid-cols-7 gap-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="px-2 py-1">{d}</div>
            ))}
          </div>
        )}
        <div className={`mt-1 grid ${gridCols} gap-1`}>
          {cells.map((d, i) => {
            if (!d)
              return (
                <div
                  key={i}
                  className={`${cellMinHeight} rounded-[var(--radius-button)] bg-[var(--color-surface)]/30`}
                />
              );
            const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            // Defensive: external dots only ever render in the My-calendar view.
            const dots = (dotsByDate.get(ymd) ?? []).filter(
              (dot) => dot.kind !== 'external' || view === 'mine',
            );
            const isToday = ymd === todayYmd;
            return (
              <div
                key={i}
                className={
                  `${cellMinHeight} rounded-[var(--radius-button)] border p-2 text-xs ` +
                  (isToday
                    ? 'border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/5'
                    : 'border-[var(--color-border)] bg-white')
                }
              >
                <div
                  className={
                    'mb-1 font-mono text-[11px] ' +
                    (isToday ? 'font-bold text-[var(--color-t3-blue)]' : 'text-[var(--color-muted)]')
                  }
                >
                  {d.getDate()}
                </div>
                <ul className="space-y-1">
                  {dots.slice(0, dotLimit).map((dot, idx) => {
                    const baseCls =
                      'truncate rounded px-1 py-0.5 text-left text-[11px] ' +
                      (dot.kind === 'due'
                        ? 'bg-[var(--color-danger)]/10 text-[var(--color-danger)]'
                        : dot.kind === 'cutover'
                          ? 'bg-[var(--color-t3-purple)]/10 text-[var(--color-t3-purple)]'
                          : dot.kind === 'checkin'
                            ? dot.done
                              ? 'bg-emerald-50 text-emerald-700 line-through'
                              : 'bg-emerald-50 text-emerald-800'
                            : dot.kind === 'milestone'
                              ? dot.done
                                ? 'bg-amber-50 text-amber-700 line-through'
                                : 'bg-amber-100 font-semibold text-amber-900'
                              : dot.kind === 'event'
                                ? 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                                : dot.kind === 'external'
                                  ? 'border border-dashed border-slate-300 bg-white text-slate-600'
                                  : dot.done
                                    ? 'bg-[var(--color-surface)] text-[var(--color-muted)] line-through'
                                    : 'bg-[var(--color-t3-blue)]/10 text-[var(--color-t3-blue)]');
                    const titleText = `${dot.label}${dot.org ? ` — ${dot.org}` : ''}`;

                    // Team events open their detail modal; everything else is a
                    // link (the org page for check-ins, Google for external
                    // events, the project for dates).
                    const main =
                      dot.kind === 'event' && dot.eventId ? (
                        <button
                          type="button"
                          title={titleText}
                          className={baseCls + ' block min-w-0 flex-1'}
                          onClick={() => setSelectedEventId(dot.eventId!)}
                        >
                          {dot.label}
                        </button>
                      ) : (
                        <Link
                          to={dot.href}
                          title={titleText}
                          className={baseCls + ' block min-w-0 flex-1'}
                        >
                          {dot.label}
                        </Link>
                      );

                    const loggedEntry = dot.logTarget
                      ? logged.get(dot.logTarget.externalCalendarEventId)
                      : undefined;

                    return (
                      <li key={idx} className="flex items-center gap-1">
                        {main}
                        {dot.meetingUrl && (
                          <a
                            href={dot.meetingUrl}
                            target="_blank"
                            rel="noreferrer"
                            title="Join Teams meeting"
                            className="shrink-0 rounded px-1 text-[10px] font-semibold text-[var(--color-t3-blue)] hover:bg-slate-200"
                          >
                            Teams →
                          </a>
                        )}
                        {dot.logTarget && loggedEntry && (
                          <Link
                            to="/timesheets"
                            title={`Logged: ${loggedEntry.durationMinutes} min · ${bucketLabel(
                              loggedEntry.billingBucket,
                            )} · ${loggedEntry.organizationName}`}
                            className="inline-flex shrink-0 items-center gap-0.5 rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-200"
                          >
                            <Check className="h-3 w-3" />
                            {loggedEntry.durationMinutes}m
                          </Link>
                        )}
                        {dot.logTarget && !loggedEntry && (
                          <button
                            type="button"
                            onClick={() => setLogTarget(dot.logTarget!)}
                            title="Log this meeting as time"
                            aria-label={`Log time for ${dot.label}`}
                            className="inline-flex shrink-0 items-center rounded border border-[var(--color-border)] bg-white p-0.5 text-[var(--color-t3-blue)] hover:border-[var(--color-t3-blue)] hover:bg-[var(--color-t3-blue)]/10"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                  {dots.length > dotLimit && (
                    <li className="text-[10px] text-[var(--color-muted)]">
                      +{dots.length - dotLimit} more
                    </li>
                  )}
                  {dots.length === 0 && scope === 'day' && (
                    <li className="text-xs italic text-[var(--color-muted)]">
                      Nothing scheduled.
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="flex flex-wrap gap-4 text-xs text-[var(--color-muted)]">
        {view === 'mine' && <Legend color="rgb(203 213 225 / 0.6)" label="My Google calendar" />}
        {view === 'team' && <Legend color="rgb(16 185 129 / 0.5)" label="Check-in" />}
        {view === 'team' && <Legend color="rgb(148 163 184 / 0.6)" label="Team event" />}
        {view === 'team' && <Legend color="var(--color-t3-blue)" label="Subtask" />}
        {view === 'team' && <Legend color="rgb(245 158 11 / 0.6)" label="Milestone" />}
        {view === 'team' && <Legend color="var(--color-danger)" label="Project due" />}
        {view === 'team' && <Legend color="var(--color-t3-purple)" label="Cutover" />}
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full px-3.5 py-1.5 text-xs font-semibold transition ' +
        (active
          ? 'bg-[var(--color-header)] text-white'
          : 'text-[var(--color-muted)] hover:text-[var(--color-header)]')
      }
    >
      {children}
    </button>
  );
}

function EventDetailModal({
  event,
  timeZone,
  logged,
  onLogTime,
  onClose,
  onCancelled,
  onUpdated,
}: {
  event: CalendarEventLite | null;
  timeZone: string;
  logged: LoggedEntry | null;
  onLogTime: (target: CalendarTimeTarget) => void;
  onClose: () => void;
  onCancelled: () => void;
  onUpdated: () => void;
}): React.ReactElement | null {
  const [busy, setBusy] = useState(false);
  const [generatingTeams, setGeneratingTeams] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit-mode state. Switching into edit hydrates these from the event.
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editDuration, setEditDuration] = useState(30);
  const [editLocation, setEditLocation] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAttendees, setEditAttendees] = useState('');

  if (!event) return null;

  function openEdit() {
    if (!event) return;
    setEditTitle(event.title);
    setEditStart(formatLocalInputInTz(new Date(event.scheduledAt), timeZone));
    setEditDuration(event.durationMinutes);
    setEditLocation(event.location ?? '');
    setEditDescription(event.description ?? '');
    setEditAttendees(event.attendeeEmails.join(', '));
    setEditing(true);
    setError(null);
  }

  async function saveEdit(): Promise<void> {
    if (!event) return;
    if (!editTitle.trim()) {
      setError('Title is required.');
      return;
    }
    const startDate = zonedInputToUtc(editStart, timeZone);
    if (Number.isNaN(startDate.getTime())) {
      setError('Start time is not a valid date.');
      return;
    }
    const emails = editAttendees
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'));

    setSavingEdit(true);
    setError(null);
    try {
      await apiFetch(`/api/calendar-events/${event.id}`, {
        method: 'PATCH',
        json: {
          title: editTitle.trim(),
          scheduledAt: startDate.toISOString(),
          durationMinutes: Math.max(5, editDuration),
          location: editLocation.trim() || null,
          description: editDescription.trim() || null,
          attendeeEmails: emails,
        },
      });
      setEditing(false);
      onUpdated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to save event.');
    } finally {
      setSavingEdit(false);
    }
  }

  const dt = new Date(event.scheduledAt);
  const dateLabel = new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(dt);
  const timeLabel = formatTimeInTz(dt, timeZone);
  const tzLabel = tzShortLabel(timeZone);

  async function cancelEvent(): Promise<void> {
    if (!event) return;
    if (!window.confirm(`Cancel "${event.title}"? Attendees will be notified.`)) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/calendar-events/${event.id}`, { method: 'DELETE' });
      onCancelled();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to cancel event.');
    } finally {
      setBusy(false);
    }
  }

  async function generateTeamsMeeting(): Promise<void> {
    if (!event) return;
    setGeneratingTeams(true);
    setError(null);
    try {
      await apiFetch(`/api/calendar-events/${event.id}/teams`, { method: 'POST' });
      onUpdated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to generate Teams meeting.');
    } finally {
      setGeneratingTeams(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-12"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-[var(--color-header)]">
            {editing ? 'Edit event' : event.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-[var(--color-muted)] hover:text-[var(--color-header)]"
          >
            ✕
          </button>
        </div>

        {!editing && (
          <>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              {dateLabel} · {timeLabel} {tzLabel} · {event.durationMinutes}m
              {event.organizerAgentName ? ` · organized by ${event.organizerAgentName}` : ''}
            </p>
            <button
              type="button"
              onClick={openEdit}
              className="mt-2 text-xs font-semibold text-[var(--color-t3-blue)] hover:underline"
            >
              Edit event
            </button>

            {event.location && (
              <div className="mt-3 text-sm">
                <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Location</span>
                <p>{event.location}</p>
              </div>
            )}

            {event.description && (
              <div className="mt-3 text-sm">
                <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Description</span>
                <p className="whitespace-pre-wrap">{event.description}</p>
              </div>
            )}

            <div className="mt-3 text-sm">
              <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
                Attendees ({event.attendeeEmails.length})
              </span>
              {event.attendeeEmails.length > 0 ? (
                <ul className="mt-1 flex flex-wrap gap-1">
                  {event.attendeeEmails.map((e) => (
                    <li
                      key={e}
                      className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-body)]"
                    >
                      {e}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-xs italic text-[var(--color-muted)]">No invitees yet.</p>
              )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-4">
              {event.teamsJoinUrl ? (
                <a
                  href={event.teamsJoinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-t3-blue)]/90"
                >
                  Join Teams meeting →
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => void generateTeamsMeeting()}
                  disabled={generatingTeams}
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-t3-blue)]/90 disabled:opacity-60"
                  title="Mint a Teams meeting URL for this event and patch the Google invite"
                >
                  {generatingTeams ? 'Generating…' : 'Generate Teams meeting'}
                </button>
              )}
              {logged ? (
                <Link
                  to="/timesheets"
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
                >
                  <Check className="h-4 w-4" />
                  Logged {logged.durationMinutes} min · {bucketLabel(logged.billingBucket)}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    onLogTime({
                      externalCalendarEventId: `t3-event::${event.id}`,
                      title: event.title,
                      startUtc: event.scheduledAt,
                      durationMinutes: event.durationMinutes,
                      organizationId: event.organizationId,
                      organizationName: event.organizationName,
                    })
                  }
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                  title="Log this meeting as time — no ticket needed"
                >
                  <Plus className="h-4 w-4" />
                  Log time
                </button>
              )}
              {event.googleHtmlLink && (
                <a
                  href={event.googleHtmlLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                >
                  Open in Google Calendar →
                </a>
              )}
              <button
                type="button"
                onClick={() => void cancelEvent()}
                disabled={busy}
                className="ml-auto text-sm text-[var(--color-danger)] hover:underline disabled:opacity-60"
              >
                {busy ? 'Cancelling…' : 'Cancel event'}
              </button>
            </div>
          </>
        )}

        {editing && (
          <div className="mt-3 space-y-3">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Title</span>
              <input
                type="text"
                required
                maxLength={500}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="block text-xs font-medium text-[var(--color-header)]">
                  Start <span className="font-normal text-[var(--color-muted)]">({tzLabel})</span>
                </span>
                <input
                  type="datetime-local"
                  required
                  value={editStart}
                  onChange={(e) => setEditStart(e.target.value)}
                  className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="block text-xs font-medium text-[var(--color-header)]">Duration (min)</span>
                <input
                  type="number"
                  min={5}
                  step={5}
                  value={editDuration}
                  onChange={(e) => setEditDuration(Math.max(5, Number(e.target.value) || 30))}
                  className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Location</span>
              <input
                type="text"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Description</span>
              <textarea
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Attendees</span>
              <textarea
                rows={2}
                value={editAttendees}
                onChange={(e) => setEditAttendees(e.target.value)}
                placeholder="alice@example.com, bob@client.com"
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                Comma- or space-separated emails. Changes sync to the Google invite with
                <code className="ml-1 rounded bg-[var(--color-surface)] px-1">sendUpdates=all</code>.
              </span>
            </label>
            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
            <div className="flex gap-2 border-t border-[var(--color-border)] pt-3">
              <Button onClick={() => void saveEdit()} disabled={savingEdit || !editTitle.trim()}>
                {savingEdit ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
                disabled={savingEdit}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!editing && error && <p className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>}
      </div>
    </div>
  );
}

function NewEventModal({
  timeZone,
  onClose,
  onCreated,
}: {
  timeZone: string;
  onClose: () => void;
  onCreated: () => void;
}): React.ReactElement {
  const tzLabel = tzShortLabel(timeZone);
  // Default start = next top-of-the-hour in the selected timezone.
  const initial = (() => {
    const nowPlusHour = new Date(Date.now() + 60 * 60 * 1000);
    const seed = formatLocalInputInTz(nowPlusHour, timeZone);
    // Snap minutes to :00 so the picker opens clean.
    return seed.slice(0, 13) + ':00';
  })();

  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState(initial);
  const [duration, setDuration] = useState(30);
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  // Two attendee channels: emails picked from the chosen org's contacts
  // (lowercased emails), and a free-text overflow for anyone else.
  const [organizationId, setOrganizationId] = useState('');
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [orgContacts, setOrgContacts] = useState<
    { id: string; name: string | null; email: string; isOrgAdmin?: boolean; isOrgBilling?: boolean }[]
  >([]);
  const [pickedContactEmails, setPickedContactEmails] = useState<Set<string>>(new Set());
  const [attendees, setAttendees] = useState('');
  const [createTeams, setCreateTeams] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ organizations: { id: string; name: string }[] }>('/api/organizations')
      .then((r) => setOrgs(r.organizations))
      .catch(() => setOrgs([]));
  }, []);

  // Load contacts whenever the org choice changes; clear picked set so
  // we don't carry over emails from a previously-chosen org.
  useEffect(() => {
    setPickedContactEmails(new Set());
    if (!organizationId) {
      setOrgContacts([]);
      return;
    }
    apiFetch<{ contacts: typeof orgContacts }>(
      `/api/organizations/${encodeURIComponent(organizationId)}/contacts`,
    )
      .then((r) => setOrgContacts(r.contacts))
      .catch(() => setOrgContacts([]));
  }, [organizationId]);

  function toggleContact(email: string) {
    const key = email.toLowerCase();
    setPickedContactEmails((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    const fromText = attendees
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'));
    // Merge picked contacts + free-text. Dedupe by lowercase email.
    const seen = new Set<string>();
    const emails: string[] = [];
    for (const e of [...pickedContactEmails, ...fromText]) {
      const key = e.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        emails.push(e);
      }
    }
    setBusy(true);
    try {
      const startUtc = zonedInputToUtc(scheduledAt, timeZone);
      if (Number.isNaN(startUtc.getTime())) {
        setError('Start time is not a valid date.');
        setBusy(false);
        return;
      }
      await apiFetch('/api/calendar-events', {
        method: 'POST',
        json: {
          title: title.trim(),
          scheduledAt: startUtc.toISOString(),
          durationMinutes: Math.max(5, duration),
          description: description.trim() || null,
          location: location.trim() || null,
          attendeeEmails: emails,
          organizationId: organizationId || null,
          createTeamsMeeting: createTeams,
        },
      });
      onCreated();
    } catch (e2) {
      setError(e2 instanceof ApiError ? e2.message : 'Failed to create event.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-12"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[var(--color-header)]">New event</h2>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Creates a Google Calendar event with attendees (real invites are sent automatically). A
          Teams meeting URL is minted via Microsoft Graph and embedded in the invite.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="block text-xs font-medium text-[var(--color-header)]">Title</span>
            <input
              type="text"
              required
              maxLength={500}
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">
                Start <span className="font-normal text-[var(--color-muted)]">({tzLabel})</span>
              </span>
              <input
                type="datetime-local"
                required
                value={scheduledAt}
                onChange={(ev) => setScheduledAt(ev.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                Interpreted in the calendar timezone ({tzLabel}). Change it in the calendar header.
              </span>
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Duration (min)</span>
              <input
                type="number"
                min={5}
                step={5}
                value={duration}
                onChange={(ev) => setDuration(Math.max(5, Number(ev.target.value) || 30))}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-[var(--color-header)]">
              Organization (optional)
            </span>
            <select
              value={organizationId}
              onChange={(ev) => setOrganizationId(ev.target.value)}
              className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            >
              <option value="">— none / internal —</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-[var(--color-muted)]">
              Pick the client org to load their contacts below for one-click invites.
            </span>
          </label>

          {organizationId && (
            <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)]/40 p-3">
              <span className="block text-xs font-medium text-[var(--color-header)]">
                Invite from this org
              </span>
              {orgContacts.length === 0 ? (
                <p className="mt-1 text-xs italic text-[var(--color-muted)]">
                  No contacts on this org yet. Add some on the org's Contacts panel.
                </p>
              ) : (
                <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded bg-white p-2">
                  {[...orgContacts]
                    .sort((a, b) => {
                      const sa = (a.isOrgAdmin ? 0 : a.isOrgBilling ? 1 : 2);
                      const sb = (b.isOrgAdmin ? 0 : b.isOrgBilling ? 1 : 2);
                      if (sa !== sb) return sa - sb;
                      return (a.name ?? a.email).localeCompare(b.name ?? b.email);
                    })
                    .map((c) => {
                      const key = c.email.toLowerCase();
                      const checked = pickedContactEmails.has(key);
                      return (
                        <label
                          key={c.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-[var(--color-surface)]"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleContact(c.email)}
                            className="h-4 w-4 accent-[var(--color-t3-blue)]"
                          />
                          <span className="truncate">
                            {c.name ? `${c.name} ` : ''}
                            <span className="text-[var(--color-muted)]">&lt;{c.email}&gt;</span>
                          </span>
                          {c.isOrgAdmin && (
                            <span className="ml-auto rounded-full bg-[var(--color-t3-blue)]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-t3-blue)]">
                              admin
                            </span>
                          )}
                        </label>
                      );
                    })}
                </div>
              )}
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                {pickedContactEmails.size} selected.
              </p>
            </div>
          )}

          <label className="block text-sm">
            <span className="block text-xs font-medium text-[var(--color-header)]">
              Other attendees (emails)
            </span>
            <input
              type="text"
              placeholder="external-partner@example.com"
              value={attendees}
              onChange={(ev) => setAttendees(ev.target.value)}
              className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            />
            <span className="mt-1 block text-xs text-[var(--color-muted)]">
              Anyone outside the org's contact list (T3 staff, external partners). Comma- or
              space-separated.
            </span>
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-[var(--color-header)]">Location (optional)</span>
            <input
              type="text"
              value={location}
              onChange={(ev) => setLocation(ev.target.value)}
              className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-[var(--color-header)]">Description (optional)</span>
            <textarea
              rows={3}
              value={description}
              onChange={(ev) => setDescription(ev.target.value)}
              className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={createTeams}
              onChange={(ev) => setCreateTeams(ev.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span>
              <span className="font-medium text-[var(--color-header)]">Create a Teams meeting</span>
              <span className="block text-xs text-[var(--color-muted)]">
                Mints a join URL via Microsoft Graph. Uncheck for a calendar-only event.
              </span>
            </span>
          </label>
          {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
          <div className="flex gap-2 border-t border-[var(--color-border)] pt-3">
            <Button type="submit" disabled={busy || !title.trim()}>
              {busy ? 'Creating…' : 'Create event'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-3 w-3 rounded" style={{ background: color, opacity: 0.5 }} />
      {label}
    </span>
  );
}
